package br.com.vvrefeicoes.portal.feature.notifications

import br.com.vvrefeicoes.portal.data.repository.decodeNotificationItem
import br.com.vvrefeicoes.portal.domain.model.AppNotification
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import io.ktor.client.HttpClient
import io.ktor.client.plugins.websocket.webSocket
import io.ktor.websocket.Frame
import io.ktor.websocket.readText
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch

/**
 * Cliente STOMP mínimo sobre WebSocket para receber notificações em tempo real.
 * Assina a fila do usuário (`/user/queue/notifications`) e o broadcast (`/topic/notifications`).
 */
interface NotificationStream {
    fun start(
        scope: CoroutineScope,
        onNotification: (AppNotification) -> Unit,
    )

    fun stop()
}

class NotificationSocket(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : NotificationStream {
    private var job: Job? = null

    override fun start(
        scope: CoroutineScope,
        onNotification: (AppNotification) -> Unit,
    ) {
        if (job?.isActive == true) return
        job = scope.launch { runLoop(onNotification) }
    }

    override fun stop() {
        job?.cancel()
        job = null
    }

    private suspend fun runLoop(onNotification: (AppNotification) -> Unit) {
        var attempt = 0
        while (currentCoroutineContext().isActive) {
            try {
                connect(onNotification) { attempt = 0 }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Throwable) {
                // reconecta com backoff
            }
            attempt++
            if (currentCoroutineContext().isActive) {
                delay(backoffMillis(attempt))
            }
        }
    }

    private suspend fun connect(
        onNotification: (AppNotification) -> Unit,
        onOpen: () -> Unit,
    ) {
        val token = authRepository.session.value?.token ?: return
        httpClient.webSocket(webSocketUrl(baseUrl)) {
            onOpen()
            send(Frame.Text(connectFrame(token)))
            send(Frame.Text(subscribeFrame("sub-feed", "/user/queue/notifications")))
            send(Frame.Text(subscribeFrame("sub-broadcast", "/topic/notifications")))
            for (frame in incoming) {
                if (frame is Frame.Text) {
                    parseMessage(frame.readText())?.let(onNotification)
                }
            }
        }
    }

    private fun parseMessage(raw: String): AppNotification? {
        val frame = raw.trimEnd('\u0000')
        if (frame.isBlank()) return null
        val headerEnd = frame.indexOf("\n\n")
        val headerPart = if (headerEnd >= 0) frame.substring(0, headerEnd) else frame
        val body = if (headerEnd >= 0) frame.substring(headerEnd + 2) else ""
        if (headerPart.substringBefore('\n').trim() != "MESSAGE") return null
        return decodeNotificationItem(body.trim('\u0000', '\n', '\r', ' '))
    }

    private fun connectFrame(token: String): String = "CONNECT\naccept-version:1.2\nhost:localhost\nAuthorization:Bearer $token\n\n\u0000"

    private fun subscribeFrame(
        id: String,
        destination: String,
    ): String = "SUBSCRIBE\nid:$id\ndestination:$destination\nack:auto\n\n\u0000"

    private fun backoffMillis(attempt: Int): Long = (1_000L shl attempt.coerceAtMost(4)).coerceAtMost(30_000L)
}

internal fun webSocketUrl(baseUrl: String): String {
    val normalized =
        when {
            baseUrl.startsWith("https://") -> "wss://" + baseUrl.removePrefix("https://")
            baseUrl.startsWith("http://") -> "ws://" + baseUrl.removePrefix("http://")
            else -> baseUrl
        }
    return normalized.trimEnd('/') + "/ws"
}
